import React, { FC, useCallback, useEffect, useState } from 'react';

import {
	EvIcon,
	EvSelectNew,
	EvTag,
	OptionType,
	RemoveIcon,
	TAG_STATUS
} from '@evinced-private/ui-common';

import { useLabelsRemoval } from '../hooks/useLabelsRemoval';
import { MODAL_ACTION_TITLES, MODAL_DESCRIPTIONS } from '../labels';
import { LABEL_MANAGEMENT_MODAL_TYPE } from '../shared-modal/constants';

import type { RemoveLabelTestsModalProps } from './RemoveLabelTestsModal.types';

import './RemoveLabelTestsModal.scss'; // create if needed

const ModalType = LABEL_MANAGEMENT_MODAL_TYPE.REMOVE;

export const RemoveLabelTestsModal: FC<RemoveLabelTestsModalProps> = ({
	testRows,
	tenantConfiguredLabels,
	configuredValues,
	onDataChange,
	appType
}) => {
	const [ariaLiveState, setAriaLiveState] = useState<string>('');

	const { selectedLabel, setSelectedLabel, removals, labelOptions, removeRemoval } =
		useLabelsRemoval({ testRows, appType, configuredValues, tenantConfiguredLabels });

	useEffect(() => {
		onDataChange({ removals });
	}, [removals, onDataChange]);

	const handleRemoveRemoval = useCallback(
		(index: number, label: OptionType) => {
			removeRemoval(index);
			setAriaLiveState(`${label.label} removed from selection`);
		},
		[removeRemoval]
	);

	const title = `${MODAL_ACTION_TITLES[ModalType]} ${testRows.length} ${testRows.length === 1 ? 'session' : 'sessions'}`;

	return (
		<>
			<div aria-live="polite" aria-atomic="true" className="sr-only">
				{ariaLiveState}
			</div>
			<div className="remove-label-tests-modal__content" role="region" aria-label={title}>
				<p className="remove-label-tests-modal__description">{MODAL_DESCRIPTIONS[ModalType]}</p>
				<EvSelectNew
					options={labelOptions}
					selectedValue={selectedLabel}
					onSelectChange={(options) => {
						setSelectedLabel(Array.isArray(options) ? options : [options]);
					}}
					placeholder="Select label to remove"
					isSearchable
					label=""
					id="remove_label_selector"
				/>
				{removals.length > 0 && (
					<>
						<div className="remove-label-tests-modal__divider" />
						<div className="remove-label-tests-modal__removals-section">
							<h3 className="remove-label-tests-modal__removals-title">Labels to remove</h3>
							<div className="remove-label-tests-modal__removals-list">
								{removals.map(({ label }, index) => (
									<div key={label.value} className="remove-label-tests-modal__removal-item">
										<EvTag status={TAG_STATUS.INFO} text={label.label} />
										<div className="remove-label-tests-modal__removal-remove-btn">
											<button
												type="button"
												className="remove-icon-button remove-label-tests-modal__removal-remove-btn"
												onClick={() => handleRemoveRemoval(index, label)}
												aria-label={`Remove label ${label.label} from selection`}
											>
												<EvIcon icon={RemoveIcon} />
											</button>
										</div>
									</div>
								))}
							</div>
						</div>
					</>
				)}
			</div>
		</>
	);
};
